import React, { useEffect, useState } from "react"
import { Comment as CommentType, LexicalText } from "@/schema"
import { useGlobalSlideoverStore } from "@/stores/use-global-slideover-store"
import { formatActivityTimestamp } from "@/utils/format-date"
import { Avatar, AvatarFallback, AvatarImage, Button } from "@ui/shad"
import { cn } from "@ui/utils"
import { Pencil, Trash2 } from "lucide-react"

import { useDeleteComment, useUpdateComment } from "@/hooks/api/comments/mutations"
import { useFetchUsers } from "@/hooks/api/users"
import { ConfirmDialog } from "@/components/dialogs"
import { EmojiPickerPopover } from "@/components/emoji-picker-popover"
import { Editor } from "@/components/lexical"
import { TaskDetailSlideover } from "@/components/tasks/detail-slideover"
import { UserAvatar } from "@/components/users/user-avatar"

import { CommentAttachments } from "./comment-attachments"
import { CommentComposer } from "./comment-composer"
import { ReactionBar } from "./reaction-bar"

interface CommentProps {
  comment: CommentType
  onReply: (commentId: string) => void
  onReaction: (commentId: string, emoji: string) => void
  currentUserId: string
  depth?: number
  isReplyFormOpen?: boolean
  isNew?: boolean
  children?: React.ReactNode
  allowMentions?: boolean
  appearance?: "card" | "thread"
  showTaskLink?: boolean
}

export function Comment({
  comment,
  onReply,
  onReaction,
  currentUserId,
  depth = 0,
  isReplyFormOpen = false,
  isNew = false,
  children,
  allowMentions = true,
  appearance = "card",
  showTaskLink = false,
}: CommentProps) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isAnimating, setIsAnimating] = useState(isNew)
  const { data: users } = useFetchUsers(allowMentions ? undefined : "")
  const deleteCommentMutation = useDeleteComment()
  const updateCommentMutation = useUpdateComment()

  const openTask = () => {
    const { isOpen, openSlideover, pushSlideover } = useGlobalSlideoverStore.getState()
    const open = isOpen ? pushSlideover : openSlideover
    open({ content: <TaskDetailSlideover taskId={comment.commentable_id} initialTab="comments" /> })
  }

  useEffect(() => {
    if (isNew) {
      const timer = setTimeout(() => setIsAnimating(false), 500)
      return () => clearTimeout(timer)
    }
  }, [isNew])

  const handleDeleteConfirm = () => {
    deleteCommentMutation.mutate({
      id: comment.id,
      commentableType: comment.commentable_type,
      commentableId: comment.commentable_id,
    })
    setShowDeleteDialog(false)
  }

  const handleEditSubmit = (content: LexicalText) => {
    updateCommentMutation.mutate({ id: comment.id, content }, { onSuccess: () => setIsEditing(false) })
  }

  const hasReplies = comment.replies && comment.replies.length > 0
  const hasReactions = comment.reactions && comment.reactions.length > 0
  const isOwnComment = comment.user_id === currentUserId

  const user = users?.users.find((user) => user.id === comment.user_id)
  const displayName = user?.full_name ?? comment.user_name
  const isSystemComment = !comment.user_id
  const isEdited = comment.edited

  return (
    <div
      className={cn(
        "group/comment transition-all duration-300",
        depth > 0 && "ml-12",
        isAnimating && "animate-in slide-in-from-top-2 fade-in duration-300"
      )}
    >
      <div
        className={cn(
          "relative grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 transition-colors",
          appearance === "card" ? "hover:bg-tinted rounded-xl border p-3" : "py-1",
          isAnimating && "ring-primary/20 ring-2"
        )}
      >
        {showTaskLink && comment.commentable_type === "Task" && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground col-span-2 mb-1 max-w-[85%] min-w-0 justify-self-start truncate text-left text-xs"
            title={`Surfaced from ${comment.task_title || "task"}`}
            onClick={openTask}
          >
            Surfaced from <span className="underline">{comment.task_title || "task"}</span>
          </button>
        )}
        {user ? (
          <UserAvatar userId={user.id} size="xs" />
        ) : isSystemComment ? (
          <UserAvatar size="xs" />
        ) : (
          <Avatar size="xs">
            <AvatarImage
              src={comment.user_profile_picture_url ?? undefined}
              alt={displayName || ""}
              className="object-cover"
            />
            <AvatarFallback className="bg-tertiary m-0 flex h-full w-full items-center justify-center overflow-hidden rounded-full p-0 text-xs font-medium">
              {displayName?.charAt(0) || "?"}
            </AvatarFallback>
          </Avatar>
        )}

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-baseline gap-2">
            <span className="text-sm font-medium">{isSystemComment ? "Slant" : displayName}</span>
            <span className="text-muted-foreground text-xs">
              {formatActivityTimestamp(comment.created_at)}
              {isEdited && " (edited)"}
            </span>
          </div>

          {isEditing ? (
            <CommentComposer
              onSubmit={handleEditSubmit}
              onCancel={() => setIsEditing(false)}
              isSubmitting={updateCommentMutation.isPending}
              initialContent={comment.content}
              submitLabel="Save"
              showMicButton={false}
              allowMentions={allowMentions}
              autoFocus
            />
          ) : (
            <div
              className="min-w-0 text-sm break-words"
              style={{ "--lexical-editor-font-size": "14px" } as React.CSSProperties}
            >
              <Editor
                key={JSON.stringify(comment.content)}
                initialContent={comment.content}
                editable={false}
                proseClasses="prose-lexical dark:prose-invert min-w-0 break-words"
              />
            </div>
          )}

          {!isEditing && <CommentAttachments attachments={comment.attachments ?? []} />}

          {hasReactions && (
            <div className="mt-2 -ml-1">
              <ReactionBar
                reactions={comment.reactions}
                currentUserId={currentUserId}
                onReaction={(emoji) => onReaction(comment.id, emoji)}
              />
            </div>
          )}
        </div>

        {!isEditing && (
          <div
            className={cn(
              "bg-background absolute top-1 right-1 flex items-center gap-1 rounded-lg border p-0.5 shadow-sm transition-opacity",
              showEmojiPicker
                ? "opacity-100"
                : "pointer-events-none opacity-0 group-focus-within/comment:pointer-events-auto group-focus-within/comment:opacity-100 group-hover/comment:pointer-events-auto group-hover/comment:opacity-100"
            )}
          >
            <EmojiPickerPopover
              open={showEmojiPicker}
              onOpenChange={setShowEmojiPicker}
              onEmojiSelect={(emoji) => onReaction(comment.id, emoji)}
              iconClassName="text-primary"
            />

            {isOwnComment && (
              <>
                <Button variant="ghost" size="iconXs" aria-label="Edit comment" onClick={() => setIsEditing(true)}>
                  <Pencil className="size-3!" />
                </Button>

                <Button
                  variant="ghost"
                  size="iconXs"
                  aria-label="Delete comment"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={deleteCommentMutation.isPending}
                  className="hover:text-destructive"
                >
                  <Trash2 className="size-3!" />
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {isReplyFormOpen && children}

      {hasReplies && depth < 3 && (
        <div className="mt-4 space-y-4">
          {comment.replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              onReply={onReply}
              onReaction={onReaction}
              currentUserId={currentUserId}
              depth={depth + 1}
              allowMentions={allowMentions}
              appearance={appearance}
              showTaskLink={showTaskLink}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={"Delete comment?"}
        description={"This action cannot be undone. This will permanently delete your comment."}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowDeleteDialog(false) }}
        action={{ label: "Delete", onClick: handleDeleteConfirm }}
      />
    </div>
  )
}
